import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Upload, Database, FileArchive, ShieldAlert, Loader2, RefreshCw, HardDrive } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Backup() {
  const [info, setInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const loadInfo = async () => {
    try {
      const { data } = await api.get("/backup/info");
      setInfo(data);
    } catch {
      toast.error("Bilgiler yüklenemedi");
    }
  };
  useEffect(() => { loadInfo(); }, []);

  const doDownload = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/backup/download", { responseType: "blob" });
      const disposition = resp.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `atolye-yedek-${Date.now()}.zip`;
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Yedek indirildi");
    } catch {
      toast.error("Yedekleme başarısız oldu");
    } finally {
      setDownloading(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Lütfen bir .zip yedek dosyası seçin");
      e.target.value = "";
      return;
    }
    setPendingFile(f);
    setConfirmOpen(true);
    e.target.value = "";
  };

  const doRestore = async () => {
    if (!pendingFile) return;
    setConfirmOpen(false);
    setRestoring(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post("/backup/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success("Geri yükleme tamamlandı");
      await loadInfo();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Geri yükleme başarısız oldu";
      toast.error(typeof detail === "string" ? detail : "Geri yükleme başarısız oldu");
    } finally {
      setRestoring(false);
      setPendingFile(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500">Ayarlar</div>
        <h1 className="text-3xl mt-1">Yedekleme & Geri Yükleme</h1>
        <div className="text-sm text-zinc-500 mt-1 max-w-2xl">
          Tüm verilerinizi (ayarlar, cari hesaplar, hareketler, faturalar, projeler, kullanıcılar ve
          yüklenen dosya/görseller dâhil) tek bir dosyaya yedekleyin. Yeni bir sürüme geçtiğinizde bu
          dosyayı geri yükleyerek kaldığınız yerden devam edin.
        </div>
      </div>

      {/* Current data snapshot */}
      <Card className="p-5 border border-zinc-200 shadow-none space-y-3" data-testid="backup-info-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database size={18} className="text-zinc-500" />
            <h3 className="text-lg font-medium">Mevcut Veri</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={loadInfo} data-testid="backup-refresh-info">
            <RefreshCw size={14} className="mr-1" />Yenile
          </Button>
        </div>
        {info ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Koleksiyon" value={info.collection_count} />
            <Stat label="Toplam Kayıt" value={info.total_documents} />
            <Stat label="Yüklenen Dosya" value={info.file_count} />
            <Stat label="Şema Sürümü" value={`v${info.schema_version}`} />
          </div>
        ) : (
          <div className="text-sm text-zinc-500">Yükleniyor...</div>
        )}
      </Card>

      {/* Storage usage */}
      <Card className="p-5 border border-zinc-200 shadow-none space-y-3" data-testid="backup-storage-card">
        <div className="flex items-center gap-2">
          <HardDrive size={18} className="text-indigo-600" />
          <h3 className="text-lg font-medium">Depolama Kullanımı</h3>
        </div>
        {info ? (
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-3xl font-semibold tabular-nums" data-testid="backup-storage-used">
                {formatBytes(info.storage_used_bytes)}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                {info.storage_file_count ?? 0} dosya diskte saklanıyor
              </div>
            </div>
            <div className="text-xs text-zinc-400 text-right max-w-[45%]">
              Yüklenen tüm görsel ve belgelerin toplam boyutu (uygulama sunucusunun diskinde).
            </div>
          </div>
        ) : (
          <div className="text-sm text-zinc-500">Yükleniyor...</div>
        )}
      </Card>

      {/* Backup */}
      <Card className="p-5 border border-zinc-200 shadow-none space-y-3" data-testid="backup-download-card">
        <div className="flex items-center gap-2">
          <FileArchive size={18} className="text-emerald-600" />
          <h3 className="text-lg font-medium">Yedek Al</h3>
        </div>
        <div className="text-sm text-zinc-500">
          Tüm sistemin anlık görüntüsünü içeren bir <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">.zip</code> dosyası indirir.
          Bu dosyayı güvenli bir yerde saklayın.
        </div>
        <div>
          <Button
            onClick={doDownload}
            disabled={downloading}
            className="bg-zinc-900 hover:bg-zinc-800"
            data-testid="backup-download-btn"
          >
            {downloading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
            {downloading ? "Hazırlanıyor..." : "Yedeği İndir"}
          </Button>
        </div>
      </Card>

      {/* Restore */}
      <Card className="p-5 border border-red-200 bg-red-50/40 shadow-none space-y-3" data-testid="backup-restore-card">
        <div className="flex items-center gap-2">
          <ShieldAlert size={18} className="text-red-600" />
          <h3 className="text-lg font-medium text-red-800">Geri Yükle</h3>
        </div>
        <div className="text-sm text-red-700/90">
          <strong>Dikkat:</strong> Geri yükleme <strong>tam değiştirme</strong> modundadır. Yedekteki tüm veriler
          mevcut verilerin <strong>üzerine yazılır ve mevcut kayıtlar silinir</strong>. Bu işlem geri alınamaz.
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={onPickFile}
          data-testid="backup-file-input"
        />
        <div>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={restoring}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
            data-testid="backup-restore-btn"
          >
            {restoring ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
            {restoring ? "Geri yükleniyor..." : "Yedek Dosyası Seç ve Geri Yükle"}
          </Button>
        </div>

        {result && (
          <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="backup-restore-result">
            <div className="font-medium">Geri yükleme tamamlandı</div>
            <div className="mt-1 text-emerald-700">
              {Object.keys(result.collections_restored || {}).length} koleksiyon,
              {" "}{result.files_restored} dosya geri yüklendi.
              {result.files_failed?.length > 0 && (
                <span className="text-amber-700"> ({result.files_failed.length} dosya yüklenemedi)</span>
              )}
            </div>
            <div className="mt-1 text-emerald-700">{result.message}</div>
          </div>
        )}
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="backup-restore-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Geri yüklemeyi onaylıyor musunuz?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{pendingFile?.name}</strong> dosyasından geri yükleme yapılacak. Mevcut tüm veriler
              silinip yedekteki verilerle değiştirilecek. Bu işlem <strong>geri alınamaz</strong>.
              Devam etmeden önce mevcut durumun yedeğini almanız önerilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-restore-cancel" onClick={() => setPendingFile(null)}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRestore}
              className="bg-red-600 hover:bg-red-700"
              data-testid="backup-restore-confirm-btn"
            >
              Evet, geri yükle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatBytes(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let val = b / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}
